import { mergeRecord } from '@ms/yammer-data-support';
import type { Reducer } from 'redux';

import type {
  GoalsOrganizationGoalsLoadMoreFulfilledAction,
  GoalsOrganizationLoadByIdFulfilledAction,
  GoalsOrganizationTeamsLoadMoreFulfilledAction,
  GoalsOrganizationsLoadFulfilledAction,
} from '../../domains/goalsOrganizations/actions.types';
import type { GroupGoalsLoadAction } from '../../domains/groupGoals/actions.types';
import type { GoalsOrganization, GoalsOrganizations } from '../types';

export const initialState: GoalsOrganizations = {};

type GoalsOrganizationsActions =
  | GoalsOrganizationsLoadFulfilledAction
  | GoalsOrganizationLoadByIdFulfilledAction
  | GoalsOrganizationGoalsLoadMoreFulfilledAction
  | GoalsOrganizationTeamsLoadMoreFulfilledAction
  | GroupGoalsLoadAction;

const addOrUpdateGoalsOrganizations = (
  state: GoalsOrganizations,
  goalsOrganizations: GoalsOrganizations | undefined
) => {
  if (!goalsOrganizations) {
    return state;
  }

  return Object.keys(goalsOrganizations).reduce(
    (newState, goalsOrganizationId) =>
      mergeRecord(newState, goalsOrganizationId, goalsOrganizations[goalsOrganizationId]),
    state
  );
};

const loadMoreTeamsFulfilled = (
  state: GoalsOrganizations,
  goalsOrganizationId: GoalsOrganization['id'],
  goalsOrganizations: GoalsOrganizations
) => {
  const incomingTeamsList = goalsOrganizations[goalsOrganizationId]?.goalsTeamsList;
  if (!incomingTeamsList) {
    return state;
  }

  const incomingTeamsIds = incomingTeamsList.goalsTeamsIds;
  const currentTeamsIds = state[goalsOrganizationId]?.goalsTeamsList?.goalsTeamsIds || [];
  const mergedTeamsIds = [...currentTeamsIds, ...incomingTeamsIds];

  const mergedGoalsTeamsList = {
    ...incomingTeamsList,
    goalsTeamsIds: mergedTeamsIds,
  };

  return mergeRecord(state, goalsOrganizationId, { goalsTeamsList: mergedGoalsTeamsList });
};
const loadMoreGoalsFulfilled = (
  state: GoalsOrganizations,
  goalsOrganizationId: GoalsOrganization['id'],
  goalsOrganizations: GoalsOrganizations
) => {
  const incomingGoalsList = goalsOrganizations[goalsOrganizationId]?.goalsList;
  if (!incomingGoalsList) {
    return state;
  }

  const incomingGoalsIds = incomingGoalsList.goalsIds;
  const currentGoalsIds = state[goalsOrganizationId]?.goalsList?.goalsIds || [];
  const mergedGoalsIds = [...currentGoalsIds, ...incomingGoalsIds];

  const mergedGoalsList = {
    ...incomingGoalsList,
    goalsIds: mergedGoalsIds,
  };

  return mergeRecord(state, goalsOrganizationId, { goalsList: mergedGoalsList });
};

const reducer: Reducer<GoalsOrganizations, GoalsOrganizationsActions> = (state = initialState, action) => {
  switch (action.type) {
    case 'GOALS_ORGANIZATIONS.LOAD_BY_ID_FULFILLED':
      return addOrUpdateGoalsOrganizations(state, action.payload.entities.goalsOrganizations);

    case 'GOALS_ORGANIZATIONS.LOAD_FULFILLED':
      return addOrUpdateGoalsOrganizations(state, action.payload.entities.goalsOrganizations);

    case 'GROUP_GOALS.LOAD_FULFILLED':
      return addOrUpdateGoalsOrganizations(state, action.payload.entities.goalsOrganizations);

    case 'GOALS_ORGANIZATIONS.LOAD_MORE_GOALS_FULFILLED':
      return loadMoreGoalsFulfilled(
        state,
        action.payload.goalsOrganizationId,
        action.payload.entities.goalsOrganizations
      );

    case 'GOALS_ORGANIZATIONS.LOAD_MORE_TEAMS_FULFILLED':
      return loadMoreTeamsFulfilled(
        state,
        action.payload.goalsOrganizationId,
        action.payload.entities.goalsOrganizations
      );

    default:
      return state;
  }
};

export default reducer;
